"use client";
import { cn } from "@frontend/shared/lib/utils";
import { useFrame, useThree } from "@react-three/fiber";
import dynamic from "next/dynamic";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

// Import shader code as raw strings since TypeScript doesn't recognize .frag/.vert files
const fragmentShader = `
 precision mediump float;

uniform float u_time;
uniform vec3 u_bg;
uniform vec3 u_colorA;
uniform vec3 u_colorB;
uniform vec2 u_mouse;

varying vec2 vUv;


vec3 mod289(vec3 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec2 mod289(vec2 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec3 permute(vec3 x) {
  return mod289(((x*34.0)+1.0)*x);
}

float snoise(vec2 v)
  {
  const vec4 C = vec4(0.211324865405187,  // (3.0-sqrt(3.0))/6.0
                      0.366025403784439,  // 0.5*(sqrt(3.0)-1.0)
                     -0.577350269189626,  // -1.0 + 2.0 * C.x
                      0.024390243902439); // 1.0 / 41.0
// First corner
  vec2 i  = floor(v + dot(v, C.yy) );
  vec2 x0 = v -   i + dot(i, C.xx);

// Other corners
  vec2 i1;
  //i1.x = step( x0.y, x0.x ); // x0.x > x0.y ? 1.0 : 0.0
  //i1.y = 1.0 - i1.x;
  i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  // x0 = x0 - 0.0 + 0.0 * C.xx ;
  // x1 = x0 - i1 + 1.0 * C.xx ;
  // x2 = x0 - 1.0 + 2.0 * C.xx ;
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;

// Permutations
  i = mod289(i); // Avoid truncation effects in permutation
  vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
    + i.x + vec3(0.0, i1.x, 1.0 ));

  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m*m ;
  m = m*m ;

// Gradients: 41 points uniformly over a line, mapped onto a diamond.
// The ring size 17*17 = 289 is close to a multiple of 41 (41*7 = 287)

  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;

// Normalise gradients implicitly by scaling m
// Approximation of: m *= inversesqrt( a0*a0 + h*h );
  m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );

// Compute final noise value at P
  vec3 g;
  g.x  = a0.x  * x0.x  + h.x  * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  
  return 80.0 * dot(m, g);
}
// End of Simplex Noise Code


void main() {
// Start with white background
  vec3 color = vec3(1.0, 1.0, 1.0);  // Changed from u_bg to white

  float noise1 = snoise(vUv * 0.7 + u_time * 0.12);
  float noise2 = snoise(vUv * 1.0 + u_time * 0.12);

  // Mix colors with reduced intensity to preserve more of the white background
  float mixStrength = 0.6; // Adjust this value to control how much of the white shows through
  color = mix(color, u_colorA, noise1 * mixStrength);
  color = mix(color, u_colorB, noise2 * mixStrength);
  
  float fadeWidth = 0.25;
  float fadeStart = smoothstep(0.0, fadeWidth, vUv.y);
  float fadeEnd = smoothstep(1.0, 1.0 - fadeWidth, vUv.y);
  float fadeMask = min(fadeStart, fadeEnd);

  // Mix with white instead of black for the fade
  color = mix(vec3(1.0), color, fadeMask);

  gl_FragColor = vec4(color, 1.0);
}
`;

const vertexShader = `
  precision mediump float;
uniform float u_aspect_ratio;

varying vec2 vUv;

void main() {
  vUv = uv - 0.1;
  vUv.x *= u_aspect_ratio;
  vUv += 0.1;

  vec4 modelPosition = modelMatrix * vec4(position, 1.0);
  vec4 viewPosition = viewMatrix * modelPosition;
  vec4 projectedPosition = projectionMatrix * viewPosition;

  gl_Position = projectedPosition;
}
`;

// Dynamically import the Canvas component with no SSR
const DynamicCanvas = dynamic(
  () => import("@react-three/fiber").then((mod) => mod.Canvas),
  {
    ssr: false,
  },
);

// Create a fallback component
const FallbackComponent: React.FC<{ colors: string[] }> = ({ colors }) => (
  <div className="relative w-full h-full overflow-hidden">
    <div
      className="absolute inset-0"
      style={{
        background: `linear-gradient(45deg, ${colors[0]}, ${colors[1]}, ${colors[2]})`,
      }}
    />
    {[...Array(8)].map((_, index) => (
      <div
        key={`fallback-color-${index}-${colors[index % colors.length]}`}
        className="absolute rounded-full mix-blend-multiply filter blur-xl animate-[spin_20s_ease-in-out_infinite]"
        style={{
          backgroundColor: colors[index % colors.length],
          width: "40%",
          height: "30%",
          top: `${Math.random() * 100 * (index % 2)}%`,
          left: `${Math.random() * 100 * (index % 2)}%`,
        }}
      />
    ))}
  </div>
);

export const BoutiqueShimmerEffect = ({
  animationSpeed = 0.4,
  opacities = [0.3, 0.3, 0.3, 0.5, 0.5, 0.5, 0.8, 0.8, 0.8, 1],
  colors,
  containerClassName,
  dotSize,
  showGradient = true,
  insuranceButtonHover,
}: {
  /**
   * 0.1 - slower
   * 1.0 - faster
   */
  animationSpeed?: number;
  opacities?: number[];
  colors: string[];
  containerClassName?: string;
  dotSize?: number;
  showGradient?: boolean;
  insuranceButtonHover: boolean;
}) => {
  const [dimensions, setDimensions] = useState({ width: 1, height: 1 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [isWebGLAvailable, setIsWebGLAvailable] = useState(false);
  // Add a new state to track if the Three.js scene is ready
  const [isSceneReady, setIsSceneReady] = useState(false);

  useEffect(() => {
    // Check WebGL availability
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    setIsWebGLAvailable(!!gl);

    const updateDimensions = () => {
      if (containerRef.current) {
        const element = containerRef.current as HTMLElement;
        const { width, height } = element.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const uniforms = useMemo(
    () => ({
      u_time: {
        value: 0.0,
      },
      u_mouse: { value: new THREE.Vector2(0, 0) },
      u_bg: {
        value: new THREE.Color(colors[0]),
      },
      u_colorA: { value: new THREE.Color(colors[1]) },
      u_colorB: { value: new THREE.Color("#FEB3D9") },
    }),
    [colors[0], colors[1]],
  );

  // Create a loading placeholder that matches your design
  const LoadingPlaceholder = () => (
    <div
      className="absolute inset-0 h-full w-full rounded-3xl"
      style={{
        background: "white",
        opacity: 1.0,
      }}
    />
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        "h-full  bg-white w-full absolute -z-1 top-0 rounded-3xl",
        containerClassName,
      )}
    >
      {(!isWebGLAvailable || !isSceneReady) && <LoadingPlaceholder />}

      {isWebGLAvailable && (
        <DynamicCanvas
          className="absolute inset-0 h-full w-full rounded-3xl"
          // Set initial clear color to match your design
          onCreated={({ gl }) => {
            gl.setClearColor("#FFFFFF", 1); // Set white background
          }}
        >
          <BlobObject
            uniforms={uniforms}
            dimensions={dimensions}
            colors={colors}
            setIsSceneReady={setIsSceneReady}
          />
        </DynamicCanvas>
      )}
    </div>
  );
};

interface Uniforms {
  [key: string]: {
    value: number | THREE.Vector2 | THREE.Color;
  };
}

interface ShaderProps {
  uniforms: Uniforms;
  dimensions: { width: number; height: number };
  colors: string[];
  setIsSceneReady: (ready: boolean) => void;
  maxFps?: number;
}

const BlobObject: React.FC<ShaderProps> = ({
  uniforms,
  dimensions,
  colors,
  setIsSceneReady,
}) => {
  const mesh = useRef<THREE.Mesh>(null);
  const { viewport } = useThree();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // Signal that the scene is ready after the first render
    setIsSceneReady(true);
  }, [setIsSceneReady]);

  useFrame((state) => {
    const { clock } = state;

    if (mesh.current) {
      const material = mesh.current.material as THREE.ShaderMaterial;
      material.uniforms.u_time.value = clock.getElapsedTime();
      material.uniforms.u_bg.value = new THREE.Color(colors[0]);
      material.uniforms.u_colorA.value = new THREE.Color(colors[1]);
      material.uniforms.u_colorB.value = new THREE.Color(colors[2]);
    }
  });

  // Calculate the scale to fit the plane to the container
  const scale = Math.min(
    viewport.width / dimensions.width,
    viewport.height / dimensions.height,
  );

  return (
    <mesh
      ref={mesh}
      position={[0, 0, 0]}
      scale={[scale * dimensions.width, scale * dimensions.height, 1]}
    >
      <planeGeometry args={[1, 1, 32, 32]} />
      <shaderMaterial
        fragmentShader={fragmentShader}
        vertexShader={vertexShader}
        uniforms={{
          ...uniforms,
          u_aspect_ratio: { value: dimensions.width / dimensions.height },
        }}
        wireframe={false}
      />
    </mesh>
  );
};
